"use client";

import { matchService } from "@/lib/services";
import { Match } from "@/lib/types";
import { motion, useInView } from "framer-motion";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

function MatchCardSkeleton() {
	return (
		<div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg animate-pulse">
			<div className="grid grid-cols-[1fr_auto_1fr] gap-6 items-center">
				<div className="flex flex-col items-center">
					<div className="w-20 h-20 md:w-24 md:h-24 mb-3 bg-gray-300 rounded-full" />
					<div className="h-4 bg-gray-300 rounded w-20" />
				</div>
				<div className="flex flex-col items-center">
					<div className="h-8 w-16 bg-gray-300 rounded mb-2" />
					<div className="h-4 bg-gray-300 rounded w-24 mb-1" />
					<div className="h-4 bg-gray-300 rounded w-28" />
				</div>
				<div className="flex flex-col items-center">
					<div className="w-20 h-20 md:w-24 md:h-24 mb-3 bg-gray-300 rounded-full" />
					<div className="h-4 bg-gray-300 rounded w-20" />
				</div>
			</div>
		</div>
	);
}

function EmptyState() {
	return (
		<div className="max-w-md mx-auto text-center">
			<div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
				<div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-10 h-10 text-white"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-bold text-white mb-2">
					No Upcoming Matches
				</h3>
				<p className="text-white/80">
					Check back soon for upcoming fixtures and match schedules.
				</p>
			</div>
		</div>
	);
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
	return (
		<div className="max-w-md mx-auto text-center">
			<div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
				<div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-10 h-10 text-white"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-bold text-white mb-2">
					Oops! Something went wrong
				</h3>
				<p className="text-white/80 mb-4">
					We couldn&apos;t load the upcoming matches.
				</p>
				<button
					onClick={onRetry}
					className="px-6 py-2 bg-white hover:bg-gray-100 text-primary font-medium rounded-lg transition-colors"
				>
					Try Again
				</button>
			</div>
		</div>
	);
}

export default function UpcomingMatchesSection() {
	const [matches, setMatches] = useState<Match[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, amount: 0.2 });

	const fetchMatches = async () => {
		setLoading(true);
		setError(false);

		try {
			const response = await matchService.getUpcoming();

			if (response.success && response.data) {
				setMatches(response.data);
			} else {
				setError(true);
			}
		} catch (err) {
			console.error("Error fetching matches:", err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchMatches();
	}, []);

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			day: "numeric",
			month: "long",
			year: "numeric",
		});
	};

	return (
		<section ref={ref} className="py-16 md:py-24 relative">
			<div className="absolute inset-0">
				<Image
					src="/assets/match.png"
					alt="Stadium background"
					fill
					className="object-cover"
				/>
				<div className="absolute inset-0 bg-indigo-900/40" />
			</div>

			<div className="container mx-auto px-4 relative z-10">
				<motion.h2
					initial={{ opacity: 0, y: -20 }}
					animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
					transition={{ duration: 0.8, ease: "easeOut" }}
					className="text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center mb-12 uppercase"
				>
					UPCOMING MATCH
				</motion.h2>

				{loading && (
					<div className="max-w-3xl mx-auto space-y-6">
						<MatchCardSkeleton />
						<MatchCardSkeleton />
					</div>
				)}

				{error && !loading && <ErrorState onRetry={fetchMatches} />}

				{!loading && !error && matches.length === 0 && <EmptyState />}

				{!loading && !error && matches.length > 0 && (
					<>
						<div className="max-w-3xl mx-auto space-y-6">
							{matches.map((match, index) => (
								<motion.div
									key={match.id}
									initial={{ opacity: 0, y: 30 }}
									animate={
										isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
									}
									transition={{
										duration: 0.6,
										delay: index * 0.15,
										ease: "easeOut",
									}}
									className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow"
								>
									<div className="grid grid-cols-[1fr_auto_1fr] gap-6 items-center">
										<div className="flex flex-col items-center">
											<div className="relative w-20 h-20 md:w-24 md:h-24 mb-3 rounded-full overflow-hidden">
												<Image
													src={match.team1_logo || "/assets/logo.png"}
													alt={match.team1_name}
													fill
													className="object-contain"
												/>
											</div>
											<p className="text-sm md:text-base font-semibold text-gray-800 text-center">
												{match.team1_name}
											</p>
										</div>

										<div className="flex flex-col items-center">
											<p className="text-3xl md:text-4xl font-bold text-primary mb-2">
												VS
											</p>
											<p className="text-primary font-bold text-sm md:text-base text-center">
												{formatDate(match.match_date)}
											</p>
											<p className="text-primary font-bold text-sm md:text-base">
												{match.match_time}
											</p>
											{match.venue && (
												<p className="text-gray-600 text-xs md:text-sm mt-1 text-center">
													{match.venue}
												</p>
											)}
										</div>

										<div className="flex flex-col items-center">
											<div className="relative w-20 h-20 md:w-24 md:h-24 mb-3 rounded-full overflow-hidden">
												<Image
													src={match.team2_logo || "/assets/logo.png"}
													alt={match.team2_name}
													fill
													className="object-contain"
												/>
											</div>
											<p className="text-sm md:text-base font-semibold text-gray-800 text-center">
												{match.team2_name}
											</p>
										</div>
									</div>
								</motion.div>
							))}
						</div>

						<motion.p
							initial={{ opacity: 0 }}
							animate={isInView ? { opacity: 1 } : { opacity: 0 }}
							transition={{
								duration: 0.6,
								delay: matches.length * 0.15 + 0.2,
								ease: "easeOut",
							}}
							className="text-center text-white/80 mt-6 text-sm"
						>
							Showing {matches.length} upcoming{" "}
							{matches.length === 1 ? "match" : "matches"}
						</motion.p>
					</>
				)}
			</div>
		</section>
	);
}
